import React, { useState, useEffect, memo, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Toast } from 'antd-mobile'

import styles from './ProductCell.module.less'
import downloadIcon from './assets/download.svg'

import ProductCellPrice from '../ProductCellPrice/ProductCellPrice'

import Thumb from '@/components/Thumb'
import SearchHighlight from '@/components/SearchHighlight/SearchHighlight'
import ShippingTypeLabel from '@/components/ShippingTypeLabel'

import { t } from '@/utils/i18n'
import { image as imageUtil, utils, productUtil, common } from '@/utils'

const { formatImgList } = imageUtil

import useCorpSettingStore from '@/stores/corp-setting'
import useRefState from '@/hooks/useRefState'
import useCorpInfoStore from '@/stores/corp-info'

import type { UnitPriceItem } from '@/types/product'

interface ProductCellProps {
  listMode: boolean
  item: any
  isFilterPage?: boolean
  index: number
  searchText: string
  onShowUnitModal: (option: object) => void
  onUpdateProd: (value: string, index: number) => void
}

interface Thumb {
  url: string
  backupUrl?: string
}

const { imgFormatter } = imageUtil
const { to, px2rem, getUuid } = utils

const ProductCell: React.FC<ProductCellProps> = memo((props) => {
  const { corpSetting } = useCorpSettingStore()

  const {
    stockOn,
    salesSlipOn,
    salesOn,
    priceOff,
    stockShowOn,
    pricePrecision,
    cloudStoreStockType,
    qualityPrecision,
  } = corpSetting

  const navigate = useNavigate()
  const params = useParams()

  const { listMode, item, isFilterPage, searchText, index, onShowUnitModal } =
    props

  const { cloudProduct = {} } = item
  const { imageList = [], video } = cloudProduct || {}

  const [thumbObj, setThumbObj] = useState<Thumb>({
    url: '',
  })

  const [lastImgUrl, setLastImgUrl] = useState<string>('')

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMultiPrice, setIsMultiPrice, isMultiPriceRef] =
    useRefState<boolean>(false)
  const [chooseUnit, setChooseUnit] = useState<UnitPriceItem>({
    priceText: '',
    originPrice: 0,
    price: 0,
    linePrice: 0,
    unitRatio: 0,
  })

  const itemClassName = `${styles['card-item']} ${
    listMode ? styles['list-style'] : styles['block-style']
  } ${isFilterPage ? styles['big'] : ''}`

  const titleClassName = `${styles['list-title']} ${
    listMode ? 'ep-2' : 'ep-2 ' + styles['big']
  }`

  const blockStyleWidth = 267
  const itemStyle = {
    width: listMode ? '100%' : px2rem(blockStyleWidth),
  }

  const { corpId } = params
  const { corpInfo } = useCorpInfoStore()
  const { paiFaFlag } = corpInfo
  const isAgent = Boolean(paiFaFlag & 16) // 是否为站点企业

  // 经过http处理后的图片地址
  const imageUrls = useMemo(() => {
    return formatImgList(imageList, video, 480)
  }, [imageList, video])

  useEffect(() => {
    const { productStatus } = props.item.cloudProduct

    setIsMultiPrice((productStatus & 128) === 128)
    initThumb()
    initChooseUnit()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.item])

  const handleGoDetail = (productId: string) => {
    navigate(`/album/product-detail/${corpId}/${productId}`)
  }

  async function initThumb() {
    const { imageList, video } = item.cloudProduct
    const imgUrl = (imageList && imageList[0]) || video

    if (imgUrl === lastImgUrl) return

    const [err, res] = await to(imgFormatter(imgUrl, 260))

    setLastImgUrl(imgUrl)
    setThumbObj({ url: err ? imgUrl : res })
  }

  // 根据默认单位索引取单位对象
  function initChooseUnit() {
    const currentChooseUnit = productUtil.setChooseUnit(
      item,
      isMultiPriceRef.current,
      cloudStoreStockType,
      qualityPrecision,
      pricePrecision,
    )
    setChooseUnit(currentChooseUnit)
  }

  function handleUnitSelect() {
    const product = item.cloudProduct

    if (stockOn && product.productStock < product.productMoqQty) {
      Toast.show(t('总库存小于起订量'))
      return
    }

    if (hasMultipleSpecs()) {
      // 如果是多规格，弹出模态修改数量框
    } else {
      // 如果是单规格，直接切换单位
      const { item: product } = props
      onShowUnitModal({
        showUnitModal: true,
        product,
        chooseUnit,
      })
    }
  }

  // 是否多规格
  function hasMultipleSpecs() {
    return (
      item.cloudProduct.specDetailList &&
      item.cloudProduct.specDetailList.length
    )
  }

  // 下载图片和视频
  const onDownloadFiles = (e: any) => {
    // 阻止事件冒泡
    e.stopPropagation()

    // 获取货品的图片和视频
    let files: { url: string; type: 'image' | 'video'; name: string }[] =
      imageUrls.map((url) => ({
        url,
        type: 'image',
        name: getUuid() + '.jpg',
      }))

    files = video
      ? files.concat({ url: video, type: 'video', name: getUuid() + '.mp4' })
      : files

    common.downloadFiles(files)
  }

  return (
    <div className={itemClassName} style={itemStyle}>
      <div className={styles['card-item-bd']}>
        <Thumb
          thumb={thumbObj}
          extClass={!listMode ? 'preview-box-block' : ''}
          width={listMode ? 200 : blockStyleWidth}
          height={listMode ? 200 : blockStyleWidth}
          onGoto={() => handleGoDetail(item.cloudProduct.productId)}
        />

        <div
          className={styles['list-content']}
          onClick={() => handleGoDetail(item.cloudProduct.productId)}
        >
          <div className={styles['list-content-top']}>
            <div className={titleClassName}>
              {searchText.length > 0 ? (
                <SearchHighlight
                  searchText={searchText}
                  productDesc={item.cloudProduct.productName}
                />
              ) : (
                <>
                  <p
                    className={productUtil.getRecommendClass(item.cloudProduct)}
                  >
                    {productUtil.getRecommendTag(item.cloudProduct)}
                  </p>
                  <span>{item.cloudProduct.productName || ''}</span>
                </>
              )}
            </div>
            {!!item.cloudProduct.productConcat && (
              <p className={styles['list-concat'] + ' ep'}>
                {item.cloudProduct.productConcat}
              </p>
            )}
            {item.cloudProduct.unitPriceArray.length > 1 && (
              <div className={styles['tag-list']}>
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>
                    {productUtil.getSpecification(
                      item.cloudProduct.unitPriceArray,
                    )}
                  </span>
                </div>
              </div>
            )}
            <div className={styles['tag-list']}>
              {stockShowOn && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>{t('库存')}</span>
                  <span className={styles['tag-text']}>
                    {chooseUnit.stock} {chooseUnit.unitName}
                  </span>
                </div>
              )}
              {/* 起订量 */}
              {item.cloudProduct.productMoqQty > 0 && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>MOQ</span>
                  <span className={styles['tag-text']}>
                    {item.cloudProduct.productMoqQty}
                    {item.cloudProduct.unitPriceArray &&
                    item.cloudProduct.unitPriceArray[
                      item.cloudProduct.productMoqUnitIndex
                    ].unitName
                      ? item.cloudProduct.unitPriceArray[
                          item.cloudProduct.productMoqUnitIndex
                        ].unitName
                      : ''}
                  </span>
                </div>
              )}
            </div>
            <div className={styles['tag-list']}>
              {/* 是否显示销量数量（基础单位） */}
              {salesOn && item.cloudProduct.saleNum != 0 && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>{t('销量')}</span>
                  <span className={styles['tag-text']}>
                    {chooseUnit.saleNum} {chooseUnit.unitName}
                  </span>
                </div>
              )}
              {/* 是否显示销售次数（销售单） */}
              {salesSlipOn && item.cloudProduct.orderCount != 0 && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>{t('销量')}</span>
                  <span className={styles['tag-text']}>
                    {item.cloudProduct.orderCount} {t('单')}
                  </span>
                </div>
              )}
            </div>

            {/* 站点企业才显示物流标签 */}
            {isAgent && (
              <div className={styles['delivery-tag']}>
                <ShippingTypeLabel
                  shippingType={item.cloudProduct.deliveryType || 0}
                />
              </div>
            )}
          </div>

          {!priceOff && (
            <div className={styles['price-container']}>
              <ProductCellPrice
                index={index}
                linePrice={chooseUnit.originPriceText}
                value={chooseUnit.priceText}
                unit={chooseUnit.priceUnitName || ''}
                fontSize={productUtil.getFontSize(chooseUnit.priceText || '')}
                isLastPrice={chooseUnit.isLastPrice}
                isMultiUnit={item.cloudProduct.unitPriceArray.length > 1}
                onTriggerUnitSelect={handleUnitSelect}
              />
            </div>
          )}
        </div>
      </div>

      {/* 下载图片按钮 */}
      <div className={styles['download-container']}>
        <div className={styles['download']} onClick={onDownloadFiles}>
          <img
            className={styles['download-icon']}
            src={downloadIcon}
            title="download"
          />
          <span>{t('下载')}</span>
        </div>
      </div>
    </div>
  )
})

export default ProductCell
